/**
 * Content Moderation Service via backend Gemini proxy
 * Checks for adult/pornographic content in images and URLs
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Helper: get current peopleId from localStorage
 * @returns {number|null}
 */
const getPeopleId = () => {
    const peopleId = parseInt(localStorage.getItem('userId'), 10);
    return peopleId || null;
};

/**
 * Convert a File/Blob to base64 string
 * @param {File|Blob} file
 * @returns {Promise<string>}
 */
const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result.split(',')[1];
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

/**
 * Check if an image contains adult/inappropriate content
 * @param {File|Blob} imageFile - The image file to check
 * @param {string} language - Language code ('fr' or 'en')
 * @returns {Promise<{safe: boolean, reason?: string}>}
 */
export const checkImageContent = async (imageFile, language = 'fr') => {
    const peopleId = getPeopleId();
    if (!peopleId) {
        console.warn('User not logged in, skipping content check');
        return { safe: true };
    }

    try {
        // Convert image to base64
        const base64Image = await fileToBase64(imageFile);
        const mimeType = imageFile.type || 'image/jpeg';

        const response = await fetch(`${API_BASE_URL}/gemini/moderate-image`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                peopleId,
                imageBase64: base64Image,
                mimeType,
                language
            })
        });

        if (response.status === 429) {
            const err = await response.json();
            console.error(`Limite Gemini atteinte (${err.hourCount}/h, ${err.dayCount}/j)`);
            return { safe: true }; // Allow on rate limit to not block users
        }

        if (!response.ok) {
            console.error('Gemini proxy error:', response.status);
            return { safe: true }; // Allow on API error to not block users
        }

        const result = await response.json();
        if (!result.success) {
            console.error('Gemini error:', result.error);
            return { safe: true };
        }

        const data = result.data || {};
        return {
            safe: data.safe === true,
            reason: data.reason || undefined
        };

    } catch (error) {
        console.error('Error checking image content:', error);
        return { safe: true }; // Allow on error to not block users
    }
};

/**
 * Check if a URL points to adult/inappropriate content
 * @param {string} url - The URL to check
 * @param {string} language - Language code ('fr' or 'en')
 * @returns {Promise<{safe: boolean, reason?: string}>}
 */
export const checkUrlContent = async (url, language = 'fr') => {
    const peopleId = getPeopleId();
    if (!peopleId) {
        console.warn('User not logged in, skipping content check');
        return { safe: true };
    }

    try {
        const response = await fetch(`${API_BASE_URL}/gemini/moderate-text`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
                peopleId,
                text: url,
                language
            })
        });

        if (response.status === 429) {
            const err = await response.json();
            console.error(`Limite Gemini atteinte (${err.hourCount}/h, ${err.dayCount}/j)`);
            return { safe: true };
        }

        if (!response.ok) {
            console.error('Gemini proxy error:', response.status);
            return { safe: true };
        }

        const result = await response.json();
        if (!result.success) {
            console.error('Gemini error:', result.error);
            return { safe: true };
        }

        const data = result.data || {};
        return {
            safe: data.safe === true,
            reason: data.reason || undefined
        };

    } catch (error) {
        console.error('Error checking URL content:', error);
        return { safe: true };
    }
};

/**
 * Cached moderation availability — populated once at app boot via initModerationAvailability().
 * `null` means "not yet checked" — `isModerationAvailable()` returns false in that state (fail-safe).
 */
let _moderationAvailable = null;

/**
 * Initialize the moderation availability cache. Call once at app boot.
 * Fetches /gemini/availability (moderation uses the same Gemini key).
 * On network/API error, sets `_moderationAvailable = false`.
 * @returns {Promise<boolean>} The resolved availability (also cached for sync access).
 */
export const initModerationAvailability = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/gemini/availability`, {
            method: 'GET',
            credentials: 'include'
        });

        if (!response.ok) {
            _moderationAvailable = false;
            return false;
        }

        const result = await response.json();
        if (!result.success) {
            _moderationAvailable = false;
            return false;
        }

        const available = result.data === true || result.data?.available === true;
        _moderationAvailable = !!available;
        return _moderationAvailable;

    } catch (error) {
        console.error('Error checking moderation availability:', error);
        _moderationAvailable = false;
        return false;
    }
};

/**
 * Synchronous getter for content moderation availability.
 * Returns false until `initModerationAvailability()` resolves (fail-safe).
 * @returns {boolean} True only if availability check completed and moderation is available.
 */
export const isModerationAvailable = () => {
    return _moderationAvailable === true;
};

export default {
    checkImageContent,
    checkUrlContent,
    isModerationAvailable,
    initModerationAvailability
};
